import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "contrast"
  | "outline"
  | "ghost"
  | "link"
  | "danger"
  | "success"
  | "completed";
export type ButtonSize = "sm" | "md" | "lg";
export type IconButtonSize = ButtonSize;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /**
   * Render the styling onto whatever single React element child the
   * caller passes (Radix `Slot`). Use this to put button styling on a
   * router-aware `<Link>` without dragging the router into the design
   * system. Disables the spinner/prefix/suffix decorators — pass a
   * single, fully-formed child.
   */
  readonly asChild?: boolean;
  /** When true, the button shows a spinner inside and is disabled. */
  readonly isLoading?: boolean;
  /** Decorative leading icon. */
  readonly iconPrefix?: React.ReactNode;
  /** Decorative trailing icon. */
  readonly iconSuffix?: React.ReactNode;
  /** Stretches to fill its parent's width (`width: 100%`). */
  readonly fullWidth?: boolean;
}

export interface IconButtonProps
  extends Omit<
    ButtonProps,
    "asChild" | "children" | "fullWidth" | "iconPrefix" | "iconSuffix" | "isLoading"
  > {
  readonly "aria-label": string;
  readonly icon: React.ReactNode;
  readonly size?: IconButtonSize;
}

/**
 * Primary CTA / button. Styling comes from `cw-v2-button*` classes in
 * `components.css`. The mono-specific additions over the cakewalk-ui
 * v2 source are `asChild` (Slot composition) and `isLoading` (spinner +
 * `aria-busy="true"` + `disabled`); everything else matches v2.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    asChild = false,
    children,
    className,
    disabled,
    fullWidth = false,
    iconPrefix,
    iconSuffix,
    isLoading = false,
    size = "md",
    type,
    variant = "primary",
    ...rest
  },
  ref,
) {
  const classes = cx(
    "cw-v2-button",
    `cw-v2-button--${variant}`,
    `cw-v2-button--${size}`,
    fullWidth && "cw-v2-button--full-width",
    className,
  );

  if (asChild) {
    return (
      <Slot ref={ref} className={classes} {...rest}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={classes}
      {...rest}
    >
      {isLoading ? (
        <span aria-hidden="true" className="cw-v2-button__icon cw-v2-button__icon--prefix">
          <svg className="cw-v2-spinner cw-v2-spinner--sm" fill="none" viewBox="0 0 24 24">
            <title>Loading</title>
            <circle
              className="cw-v2-spinner__track"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="cw-v2-spinner__indicator"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              fill="currentColor"
            />
          </svg>
        </span>
      ) : iconPrefix ? (
        <span className="cw-v2-button__icon cw-v2-button__icon--prefix">{iconPrefix}</span>
      ) : null}
      {children}
      {iconSuffix ? (
        <span className="cw-v2-button__icon cw-v2-button__icon--suffix">{iconSuffix}</span>
      ) : null}
    </button>
  );
});
Button.displayName = "Button";

/**
 * Square icon-only button matching the cakewalk-ui v2 `V2IconButton`
 * treatment. Use only when the icon has a clear accessible label.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    "aria-label": ariaLabel,
    className,
    icon,
    size = "md",
    title,
    type = "button",
    variant = "ghost",
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      aria-label={ariaLabel}
      className={cx(
        "cw-v2-button",
        "cw-v2-icon-button",
        `cw-v2-button--${variant}`,
        `cw-v2-icon-button--${size}`,
        className,
      )}
      title={title ?? ariaLabel}
      type={type}
      {...props}
    >
      <span className="cw-v2-button__icon">{icon}</span>
    </button>
  );
});
IconButton.displayName = "IconButton";
